import { CardPage, ListPage, PageHeader, StaticPage } from "./components";
import { ErrorMessage, ProgressIndicator } from "../../../widgets";
import { FC, useCallback } from "react";
import { IPage, IPageView, PageType } from "../models";
import { PageContextProvider, PageDataContextProvider, useDataContext } from "../context";

import { DesignButton } from "./components/DesignButton";
import { FetchStatus } from "../../../base/types";
import clsx from "clsx";

interface IPageProps {
    page: IPage,
    pageView: IPageView,
    showTitle: boolean,
    showRefresh: boolean,
    showHeader?: boolean
    className?: string
}

// Renders the page container with context providers
export const PageViewer: FC<IPageProps> = ({
    page, pageView, className, showTitle, showRefresh, showHeader = true
}) => {
    return (
        <PageContextProvider page={page} pageView={pageView}>
            <PageDataContextProvider page={page} pageView={pageView}>
                <div className={clsx(className, "d-flex flex-column gap-5")}>
                    {(showHeader) && <>
                        <PageHeader page={page} showTitle={showTitle} showRefresh={showRefresh} />
                    </>}
                    <PageBody page={page} design={true} />
                </div>
            </PageDataContextProvider>
        </PageContextProvider>
    )
}

interface IPageBodyProps {
    page: IPage,
    design: boolean
}

// Renders the appropriate page component based on the page type
const PageBody: FC<IPageBodyProps> = ({ page, design }) => {
    // Fetches data based on the source table and the filters provided in the page
    const { status, error } = useDataContext()

    const renderPageComponent = useCallback(() => {
        switch (page.type) {
            case PageType.Card:
            case PageType.CardPart:
                return <CardPage page={page} />;
            case PageType.List:
            case PageType.ListPart:
                return <ListPage page={page} />;
            default:
                return page.sourceObject ?
                    <CardPage page={page} /> :
                    <StaticPage page={page} />;
        }
    }, [page]);

    if (status === FetchStatus.Idle || status === FetchStatus.Loading) {
        return (
            <ProgressIndicator show={status === FetchStatus.Loading} message="Loading..." />
        )
    }

    if (status === FetchStatus.Error) {
        return (
            <ErrorMessage message={error || "An error occurred while fetching data."} />
        )
    }

    return (
        <div className="position-relative">
            {design && <DesignButton pageId={page.id} />}
            <div>{renderPageComponent()}</div>
        </div>
    )
}